import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Boxes, ShoppingBag, GitBranch, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "../components/app/AppHeader";
import TemplateProviderDialog from "../components/app/TemplateProviderDialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { STARTER_TEMPLATES } from "../lib/starterTemplates";

const PROVIDER_LABEL = { aws: "AWS", azure: "Azure", gcp: "GCP" };

const TIER_DOTS = [
  { tier: "Frontend", color: "var(--tier-frontend)" },
  { tier: "Compute", color: "var(--tier-compute)" },
  { tier: "Data", color: "var(--tier-data)" },
  { tier: "Networking", color: "var(--tier-networking)" },
  { tier: "Security", color: "var(--tier-security)" },
];

const TEMPLATE_ICONS = {
  "rest-api": Boxes,
  ecommerce: ShoppingBag,
  "serverless-pipeline": GitBranch,
};

function MiniStack({ accent }) {
  // A small abstract preview that hints at three-tier architecture.
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        height: 132,
        borderRadius: 12,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%), var(--color-background-tertiary)",
        border: "1px solid var(--color-border-secondary)",
      }}
    >
      <div
        aria-hidden
        className="absolute"
        style={{
          left: -40,
          top: -40,
          width: 220,
          height: 220,
          borderRadius: 9999,
          background: `radial-gradient(closest-side, ${accent}33, transparent 70%)`,
          filter: "blur(4px)",
        }}
      />
      <div className="relative h-full flex items-center justify-around px-5">
        {TIER_DOTS.map((d) => (
          <div key={d.tier} className="flex flex-col items-center gap-2">
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 9999,
                background: d.color,
                boxShadow: `0 0 0 4px ${d.color}22`,
                display: "inline-block",
              }}
            />
            <span
              className="font-mono"
              style={{
                fontSize: 9,
                color: "var(--color-text-tertiary)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {d.tier.slice(0, 4)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateCard({ template, onUse }) {
  const Icon = TEMPLATE_ICONS[template.id] || Boxes;
  return (
    <div
      className="rounded-[16px] border flex flex-col card-lift motion-canvas"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        padding: 24,
      }}
      data-testid={`templates-page-card-${template.id}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className="grid place-items-center"
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: `${template.accent}1F`,
            border: `1px solid ${template.accent}66`,
            color: template.accent,
          }}
        >
          <Icon size={20} strokeWidth={1.75} />
        </span>
        <span
          className="font-mono"
          style={{
            padding: "4px 10px",
            borderRadius: 9999,
            fontSize: 10,
            letterSpacing: "0.1em",
            color: "var(--color-text-tertiary)",
            background: "var(--color-background-tertiary)",
            border: "1px solid var(--color-border-secondary)",
            textTransform: "uppercase",
          }}
        >
          {template.nodeCount} services
        </span>
      </div>

      <h3
        className="text-text-primary"
        style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.015em" }}
      >
        {template.title}
      </h3>
      <p
        className="text-text-secondary mt-2"
        style={{ fontSize: 13, lineHeight: 1.55 }}
      >
        {template.description}
      </p>

      <div className="mt-5">
        <MiniStack accent={template.accent} />
      </div>

      <ul className="mt-5 space-y-2.5">
        {template.bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-text-secondary"
            style={{ fontSize: 13, lineHeight: 1.5 }}
          >
            <span
              aria-hidden
              style={{
                width: 5,
                height: 5,
                borderRadius: 9999,
                background: "var(--color-accent)",
                marginTop: 8,
                flexShrink: 0,
              }}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onUse(template)}
        data-testid={`templates-page-use-${template.id}`}
        className="motion-micro inline-flex items-center justify-center gap-2 mt-6 w-full"
        style={{
          background: "var(--color-accent)",
          color: "#0a0a0a",
          height: 44,
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.18) inset, 0 14px 32px -16px color-mix(in oklab, var(--color-accent) 65%, transparent)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
      >
        Use template <ArrowRight size={16} strokeWidth={2.25} />
      </button>
    </div>
  );
}

/** A template the user saved from one of their own projects. */
function MyTemplateCard({ template, onUse, onDelete }) {
  return (
    <div
      className="rounded-[12px] border flex flex-col"
      data-testid={`my-template-${template.id}`}
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        padding: 18,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h3
          className="text-text-primary"
          style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}
        >
          {template.name}
        </h3>
        <span
          className="font-mono shrink-0"
          style={{
            padding: "3px 8px",
            borderRadius: 9999,
            fontSize: 10,
            letterSpacing: "0.08em",
            color: "var(--color-text-tertiary)",
            background: "var(--color-background-tertiary)",
            border: "1px solid var(--color-border-secondary)",
          }}
        >
          {PROVIDER_LABEL[template.provider] || template.provider}
        </span>
      </div>

      {template.description && (
        <p className="text-text-secondary mt-2" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {template.description}
        </p>
      )}
      <p className="text-text-tertiary mt-2" style={{ fontSize: 12 }}>
        {(template.architecture || []).length} services
      </p>

      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={onUse}
          data-testid={`my-template-use-${template.id}`}
          className="motion-micro inline-flex items-center justify-center gap-2 flex-1"
          style={{
            background: "var(--color-accent)",
            color: "#0a0a0a",
            height: 36,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
          }}
        >
          Use template <ArrowRight size={14} strokeWidth={2.25} />
        </button>
        <button
          onClick={onDelete}
          data-testid={`my-template-delete-${template.id}`}
          aria-label={`Delete template ${template.name}`}
          className="motion-micro grid place-items-center"
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: "transparent",
            border: "1px solid var(--color-border-primary)",
            color: "var(--color-text-tertiary)",
            cursor: "pointer",
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default function Templates() {
  const navigate = useNavigate();
  // Which starter template is awaiting a provider choice (drives the dialog).
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [myTemplates, setMyTemplates] = useState([]);

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/templates/mine");
      setMyTemplates(data.items || []);
    } catch {
      // A failure here shouldn't blank the starter templates below it.
      setMyTemplates([]);
    }
  }, []);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  // NB: not named `useMine` — the `use` prefix makes ESLint's rules-of-hooks
  // treat it as a custom hook and reject the call inside a JSX callback.
  const createFromSaved = async (tpl) => {
    try {
      const { data: project } = await api.post("/projects/from-my-template", {
        template_id: tpl.id,
      });
      toast.success("Project created", { description: `From “${tpl.name}”.` });
      navigate(`/projects/${project.id}/output`);
    } catch (e) {
      toast.error("Could not use template", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const deleteSaved = async (tpl) => {
    try {
      await api.delete(`/templates/mine/${tpl.id}`);
      setMyTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary" data-testid="templates-page">
      <AppHeader />

      <main className="max-w-[1200px] mx-auto px-6 py-14">
        <div className="max-w-[680px]">
          <p
            className="font-mono text-text-tertiary mb-3"
            style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            Starter templates
          </p>
          <h1
            className="text-text-primary"
            style={{
              fontSize: "clamp(32px, 4vw, 44px)",
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
            }}
            data-testid="templates-headline"
          >
            Start from a proven architecture
          </h1>
          <p
            className="text-text-secondary mt-4"
            style={{ fontSize: 15, lineHeight: 1.55 }}
          >
            Pick a template and a cloud, and we load the whole architecture instantly — no
            conversation, no waiting.
          </p>
        </div>

        {myTemplates.length > 0 && (
          <section className="mt-12" data-testid="my-templates-section">
            <h2
              className="text-text-primary"
              style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.015em" }}
            >
              Your templates
            </h2>
            <div
              className="mt-5 grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
              data-testid="my-templates-grid"
            >
              {myTemplates.map((t) => (
                <MyTemplateCard
                  key={t.id}
                  template={t}
                  onUse={() => createFromSaved(t)}
                  onDelete={() => deleteSaved(t)}
                />
              ))}
            </div>
          </section>
        )}

        <div
          className="mt-12 grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
          data-testid="templates-grid"
        >
          {STARTER_TEMPLATES.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              // Opens the provider picker, which calls the instant, LLM-free
              // /projects/from-template. This used to navigate into the chat
              // wizard, which spent the same credit but took ~30s of LLM time to
              // arrive at output the backend already had hardcoded.
              onUse={(tmpl) => setPendingTemplate(tmpl)}
            />
          ))}
        </div>

        {pendingTemplate && (
          <TemplateProviderDialog
            template={pendingTemplate}
            onClose={() => setPendingTemplate(null)}
            onCreated={(project) => {
              setPendingTemplate(null);
              navigate(`/projects/${project.id}/output`);
            }}
          />
        )}

        <div
          className="mt-16 rounded-[12px] border flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            background: "var(--color-background-secondary)",
            borderColor: "var(--color-border-primary)",
            padding: 24,
          }}
        >
          <div>
            <h2
              className="text-text-primary"
              style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.005em" }}
            >
              Need something more custom?
            </h2>
            <p
              className="text-text-secondary mt-1"
              style={{ fontSize: 13, lineHeight: 1.55 }}
            >
              Describe what you&apos;re building in plain English — our generator maps it to the right cloud services automatically.
            </p>
          </div>
          <button
            onClick={() => navigate("/projects/new")}
            data-testid="templates-blank-cta"
            className="motion-micro inline-flex items-center justify-center gap-2"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-primary)",
              height: 42,
              padding: "0 18px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-border-primary)")}
          >
            Start from blank <ArrowRight size={14} strokeWidth={2.25} />
          </button>
        </div>
      </main>
    </div>
  );
}
